// pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
  Alert,
  Divider,
  Avatar,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  Bolt,
  Target,
  Timeline,
  ShowChart,
  Assessment,
  Speed,
  Psychology,
  AutoGraph,
  Warning,
  CheckCircle,
  ArrowForward,
  Notifications
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useIEP } from '../contexts/IEPContext';
import TransformationCard from '../components/IEP/TransformationCard';
import MaturityGauge from '../components/IEP/MaturityGauge';
import QuickActions from '../components/IEP/QuickActions';
import ChallengeDiagnosis from '../components/IEP/ChallengeDiagnosis';

const Dashboard = ({ organization }) => {
  const { iepEngine, recommendations, roadmap, maturityScore } = useIEP();
  const [activeTransformations, setActiveTransformations] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showChallengeDiagnosis, setShowChallengeDiagnosis] = useState(false);

  useEffect(() => {
    if (organization && iepEngine) {
      const recs = iepEngine.getRecommendedTransformations(organization, 5);
      setActiveTransformations(recs);
      
      // Generate sample performance data
      const data = generatePerformanceData(organization);
      setPerformanceData(data);
      
      // Generate alerts
      const newAlerts = generateAlerts(recs, maturityScore);
      setAlerts(newAlerts);
    }
  }, [organization, iepEngine, maturityScore]);

  const generatePerformanceData = (org) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => ({
      month,
      maturity: 30 + (index * 5) + Math.random() * 10,
      efficiency: 40 + (index * 4) + Math.random() * 8,
      innovation: 25 + (index * 6) + Math.random() * 12,
      revenue: 50 + (index * 8) + Math.random() * 15,
    }));
  };

// In pages/Dashboard.js, add this to show roadmap progress:

const roadmapCount = roadmap?.phases?.reduce((sum, phase) => 
  sum + (phase.transformations?.length || 0), 0) || 0;


  const generateAlerts = (recs, score) => {
    const alerts = [];
    
    if (score?.overallScore < 40) {
      alerts.push({
        type: 'warning',
        message: 'Low maturity score detected. Focus on foundational transformations.',
        icon: <Warning />
      });
    }
    
    if (recs?.some(r => r.priority === 'critical')) {
      alerts.push({
        type: 'error',
        message: `${recs.filter(r => r.priority === 'critical').length} critical transformations pending`,
        icon: <Warning />
      });
    }
    
    if (roadmap?.resourceAllocation?.totalBudget > 500000) {
      alerts.push({
        type: 'info',
        message: 'High-budget roadmap detected. Consider AI optimization.',
        icon: <Psychology />
      });
    }
    
    return alerts;
  };

  const categoryColors = {
    strategic: '#6366f1',
    operational: '#10b981',
    technical: '#3b82f6',
    cultural: '#8b5cf6',
    innovation: '#f59e0b',
    financial: '#ef4444',
  };

  if (!maturityScore) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h1" gutterBottom>
            Welcome back, {organization?.name || 'Leader'}!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Your transformation journey continues. {maturityScore?.overallScore >= 70 ? 'Excellent progress!' : 'Time to accelerate!'}
          </Typography>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<Assessment />}
            onClick={() => setShowChallengeDiagnosis(true)}
            sx={{ mr: 2 }}
          >
            New Diagnosis
          </Button>
          <Button 
            variant="contained" 
            endIcon={<ArrowForward />}
            href="/roadmap"
          >
            Continue Roadmap
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Box mb={3}>
          {alerts.map((alert, index) => (
            <Alert 
              key={index}
              severity={alert.type}
              icon={alert.icon}
              sx={{ mb: 1 }}
              action={
                <Button color="inherit" size="small">
                  View
                </Button>
              }
            >
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Assessment sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h3">{maturityScore?.overallScore || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Maturity Score</Typography>
                </Box>
      <Box>
          <Typography variant="h3">{roadmapCount}</Typography>
          <Typography variant="body2" color="text.secondary">
            In Roadmap
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2">
        {roadmapCount > 0 ? `${roadmapCount} transformations planned` : 'No transformations in roadmap'}
      </Typography>
              <LinearProgress 
                variant="determinate" 
                value={maturityScore?.overallScore || 0} 
                sx={{ height: 8, borderRadius: 4 }}
                color={maturityScore?.overallScore > 70 ? 'success' : maturityScore?.overallScore > 40 ? 'warning' : 'error'}
              />
              <Typography variant="caption" color="text.secondary" mt={1}>
                Level: <Chip label={maturityScore?.maturityLevel || 'loading'} size="small" color="primary" />
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Bolt sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h3">{activeTransformations.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Transformations</Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {activeTransformations.filter(t => t.priority === 'critical').length} critical
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h3">
                    {roadmap?.resourceAllocation?.roiEstimate?.roiPercentage || '0'}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Expected ROI</Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                ${(roadmap?.resourceAllocation?.totalBudget / 1000000).toFixed(1)}M investment
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Speed sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h3">Q*</Typography>
                  <Typography variant="body2" color="text.secondary">AI Optimization</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="success.main">
                14336 dimensions active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Maturity & Performance */}
        <Grid item xs={12} md={8}>
          {/* Maturity Gauge */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Organizational Maturity
              </Typography>
              <MaturityGauge score={maturityScore.overallScore} />
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Your organization is at <strong>{maturityScore.maturityLevel}</strong> level.
                  {maturityScore.overallScore < 50 && ' Focus on foundational transformations.'}
                  {maturityScore.overallScore >= 50 && maturityScore.overallScore < 80 && ' Accelerate strategic initiatives.'}
                  {maturityScore.overallScore >= 80 && ' Optimize for transcendent performance.'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Performance Trend */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                    labelStyle={{ color: '#f1f5f9' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="maturity" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="efficiency" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="innovation" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="revenue" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Recommended Transformations */}
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
                  AI-Recommended Transformations
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  startIcon={<AutoGraph />}
                  href="/transformations"
                >
                  View All
                </Button>
              </Box>
              <Grid container spacing={2}>
                {activeTransformations.slice(0, 3).map((transformation) => (
                  <Grid item xs={12} md={4} key={transformation.id}>
                    <TransformationCard 
                      transformation={transformation}
                      compact
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Quick Stats & Actions */}
        <Grid item xs={12} md={4}>
          {/* Category Performance */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Category Performance
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={Object.entries(maturityScore?.categoryScores || {}).map(([category, data]) => ({
                      name: category,
                      value: data.score,
                      count: data.count
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value.toFixed(1)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(maturityScore?.categoryScores || {}).map(([category], index) => (
                      <Cell key={`cell-${index}`} fill={categoryColors[category] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <QuickActions organization={organization} />
            </CardContent>
          </Card>

          {/* Roadmap Progress */}
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Roadmap Progress
              </Typography>
              {roadmap?.phases?.map((phase, index) => (
                <Box key={index} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Phase {index + 1}: {phase.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {phase.transformations?.length || 0} trans.
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={index === 0 ? 30 : index === 1 ? 10 : 0}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ))}
              <Button 
                fullWidth 
                variant="outlined" 
                sx={{ mt: 2 }}
                href="/roadmap"
              >
                View Full Roadmap
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AI Insights */}
      <Alert 
        severity="info" 
        sx={{ mt: 3 }}
        icon={<Psychology />}
      >
        <Typography variant="body2">
          <strong>AI Insight:</strong> Based on your current maturity level, we recommend starting with 
          foundational transformations in {Object.entries(maturityScore?.categoryScores || {})
            .sort((a, b) => a[1].score - b[1].score)[0]?.[0] || 'strategic'} category 
          to achieve maximum impact with minimal resources.
        </Typography>
      </Alert>

      {/* Challenge Diagnosis Modal */}
      {showChallengeDiagnosis && (
        <ChallengeDiagnosis
          organization={organization}
          onClose={() => setShowChallengeDiagnosis(false)}
          onComplete={(results) => {
            console.log('New diagnosis completed:', results);
            setShowChallengeDiagnosis(false);
          }}
        />
      )}
    </Box>
  );
};

export default Dashboard;